import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { API_ENDPOINTS } from '../config/api.config';
import { AuthService } from './auth.service';
import {
  ReconsultaVentasPendientesRequest,
  SunatReconsultaError,
  SunatSyncSummary,
} from '../models/sunat.model';

export const SUNAT_RECONSULTA_LIMIT_DEFAULT = 200;
export const SUNAT_RECONSULTA_LIMIT_MIN = 1;
export const SUNAT_RECONSULTA_LIMIT_MAX = 500;

const SUNAT_RECONSULTA_PATH = '/reconsulta-ventas-pendientes';

/**
 * Acciones operativas de SUNAT que antes solo eran accesibles por Swagger/curl.
 * No reutiliza `EmpresaService` a propósito: este endpoint vive en `/api/sunat`
 * y tiene su propio contrato de entrada/salida.
 */
@Injectable({
  providedIn: 'root',
})
export class SunatService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly apiUrl = API_ENDPOINTS.sunat;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  /**
   * Dispara la reconsulta manual de ventas en `SUNAT_PENDIENTE` /
   * `SUNAT_PENDIENTE_ANULACION` para una empresa.
   */
  reconsultarVentasPendientes(
    empresaId: number,
    limit: number = SUNAT_RECONSULTA_LIMIT_DEFAULT,
  ): Observable<SunatSyncSummary> {
    const normalizedEmpresaId = this.normalizeEmpresaId(empresaId);

    if (normalizedEmpresaId === null) {
      return throwError(() =>
        this.buildError(
          'Debes indicar una empresa válida para reconsultar SUNAT.',
          0,
          { empresaId },
        ),
      );
    }

    const payload: ReconsultaVentasPendientesRequest = {
      empresa_id: normalizedEmpresaId,
      limit: this.normalizeLimit(limit),
    };

    this.logAuditoria('inicio', payload);

    return this.http
      .post<SunatSyncSummary | { data?: SunatSyncSummary }>(
        `${this.apiUrl}${SUNAT_RECONSULTA_PATH}`,
        payload,
        this.httpOptions,
      )
      .pipe(
        map((response) => this.normalizeSummary(response, payload)),
        tap((summary) => this.logAuditoria('fin', { ...payload, resumen: summary })),
        catchError((error) => {
          const normalizedError = this.normalizeError(error);
          this.logAuditoria('error', {
            ...payload,
            error: normalizedError.message,
          });
          return throwError(() => normalizedError);
        }),
      );
  }

  /**
   * El backend responde el resumen en ceros cuando ya hay una reconsulta en
   * curso (lock `isRunning`) y no distingue el motivo. Mientras no exponga
   * `en_curso` o un 409, la UI usa esta heurística solo para avisar.
   */
  esResumenEnCeros(summary: SunatSyncSummary): boolean {
    return (
      summary.procesadas === 0 &&
      summary.actualizadas === 0 &&
      summary.sin_cambios === 0 &&
      summary.sin_token === 0 &&
      summary.errores === 0 &&
      summary.duracion_ms === 0
    );
  }

  private normalizeEmpresaId(empresaId: number): number | null {
    const parsed = Math.trunc(Number(empresaId));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private normalizeLimit(limit: number): number {
    const parsed = Math.trunc(Number(limit));

    if (!Number.isFinite(parsed)) {
      return SUNAT_RECONSULTA_LIMIT_DEFAULT;
    }

    return Math.min(
      SUNAT_RECONSULTA_LIMIT_MAX,
      Math.max(SUNAT_RECONSULTA_LIMIT_MIN, parsed),
    );
  }

  private normalizeSummary(
    response: SunatSyncSummary | { data?: SunatSyncSummary } | null,
    request: ReconsultaVentasPendientesRequest,
  ): SunatSyncSummary {
    const raw =
      (response as { data?: SunatSyncSummary } | null)?.data ?? response;
    const data = (raw ?? {}) as Partial<SunatSyncSummary>;

    return {
      origen: typeof data.origen === 'string' ? data.origen : 'manual',
      empresa_id: this.toNumber(data.empresa_id, request.empresa_id),
      limit: this.toNumber(data.limit, request.limit ?? SUNAT_RECONSULTA_LIMIT_DEFAULT),
      procesadas: this.toNumber(data.procesadas),
      actualizadas: this.toNumber(data.actualizadas),
      sin_cambios: this.toNumber(data.sin_cambios),
      sin_token: this.toNumber(data.sin_token),
      errores: this.toNumber(data.errores),
      duracion_ms: this.toNumber(data.duracion_ms),
      en_curso: data.en_curso === true,
    };
  }

  private toNumber(value: unknown, fallback = 0): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private normalizeError(error: unknown): SunatReconsultaError {
    const httpError = error as {
      error?: { message?: unknown };
      message?: unknown;
      status?: unknown;
    };

    const backendMessage = httpError?.error?.message;
    const joinedMessage = Array.isArray(backendMessage)
      ? backendMessage.join('. ')
      : backendMessage;
    const fallbackMessage =
      typeof httpError?.message === 'string' ? httpError.message : '';

    const message =
      typeof joinedMessage === 'string' && joinedMessage.trim()
        ? joinedMessage
        : fallbackMessage.trim() ||
          'No se pudo reconsultar el estado SUNAT de las ventas pendientes.';

    return this.buildError(
      message,
      typeof httpError?.status === 'number' ? httpError.status : 0,
      error,
    );
  }

  private buildError(
    message: string,
    status: number,
    details: unknown,
  ): SunatReconsultaError {
    return {
      success: false,
      message,
      status,
      details,
    };
  }

  private logAuditoria(
    etapa: 'inicio' | 'fin' | 'error',
    payload: object,
  ): void {
    const user = this.authService.getCurrentUser();

    console.info('[SUNAT] Reconsulta de ventas pendientes', {
      etapa,
      ejecutado_por: user?.email ?? 'desconocido',
      ejecutado_por_id: user?.id ?? null,
      ...payload,
    });
  }
}
